import styles from './FilterPanel.module.scss';
import { DrawerForm, DrawerPanel } from '@dmc/ui';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VdsButton } from '@volt/volt-extended-components-react';
import { clearDraftFilters, saveFilters } from '@customer/store/slices/filtersSlice';
import {
  FilterConfig,
  FilterElement,
  FilterElementType,
  FilterGroup,
  FilterName,
  FilterWithOptions,
} from '@customer/filters/types';
import { getFilterConfig, getFiltersList } from '@customer/filters/utils';
import { useHasDraftFilters } from '@customer/filters/useHasFilters';

interface RefItemModel<T extends FilterElementType, E extends FilterElement> {
  current: T | null;
  elementType: E;
}

type InputRefItem = RefItemModel<HTMLVdsBasicInputElement, FilterElement.VdsInput>;
type DropdownRefItem = RefItemModel<HTMLVdsBasicDropdownElement, FilterElement.VdsDropdown>;
type DropdownMultiselectRefItem = RefItemModel<
  HTMLVdsBasicDropdownMultiselectElement,
  FilterElement.VdsDropdownMultiselect
>;

type RefItem = InputRefItem | DropdownRefItem | DropdownMultiselectRefItem;

type FilterRefCallback = (element: FilterElementType | null) => void;

type ConfiguredFilter = FilterConfig &
  Omit<FilterWithOptions, 'labelTransKey'> & {
    label?: string;
    refCallback: FilterRefCallback;
  };

interface FilterPanelProps {
  filterGroup: FilterGroup;
}

export function FilterPanel({ filterGroup }: FilterPanelProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { siteId } = useAppSelector(state => state.auth);

  const filters = useMemo(() => {
    return getFiltersList(filterGroup);
  }, [filterGroup]);

  // Init the ref as an empty object, and append a dynamic set of refs to the object using refCallbacks that are provided to each field.
  const refsByKey = useRef<Partial<Record<FilterName, RefItem>>>({});

  const setRefItem = useCallback(
    (key: FilterName, element: FilterElementType | null, elementType: FilterElement) => {
      switch (elementType) {
        case FilterElement.VdsDropdown:
          refsByKey.current[key] = {
            current: element ? (element as HTMLVdsBasicDropdownElement) : null,
            elementType,
          };
          break;
        case FilterElement.VdsDropdownMultiselect:
          refsByKey.current[key] = {
            current: element ? (element as HTMLVdsBasicDropdownMultiselectElement) : null,
            elementType,
          };
          break;
        case FilterElement.VdsInput:
          refsByKey.current[key] = {
            current: element ? (element as HTMLVdsBasicInputElement) : null,
            elementType,
          };
          break;
      }
    },
    [],
  );

  // There is an odd behaviour when the 'clear all' button is clicked twice in a row.
  // The first clear action seems to return the filter to the default value, but then the second clears all values and shows the placeholder text.
  // Note: A workaround was introduced to prevent this VDS issue: the clear button is disabled unless the state has changed.
  const onClearAll = useCallback(() => {
    const refItems: RefItem[] = Object.values(refsByKey.current);

    // Loop over all refItems and use the relavant element method to clear the associated input.
    for (let i = 0; i < refItems.length; i++) {
      const refItem = refItems[i];
      if (refItem.current) {
        refItem.current.clear();
      }
    }

    dispatch(clearDraftFilters());
  }, [dispatch]);

  // Convert a subset of the filtersConfig list into a list of configured filters.
  const configuredFilters = useMemo<ConfiguredFilter[]>(() => {
    const configuredFilters: ConfiguredFilter[] = [];

    for (let i = 0; i < filters.length; i++) {
      const filter: FilterName | FilterWithOptions = filters[i];
      const isFilterWithOptions = typeof filter !== 'string';
      const filterConfig: FilterConfig | undefined = getFilterConfig(filter);

      if (filterConfig) {
        // Execute the label translation callback if one was provided.
        const label: string | undefined =
          isFilterWithOptions && typeof filter.labelTransKey !== 'undefined'
            ? t(filter.labelTransKey)
            : undefined;

        // Default to full width if no width option is provided.
        const fullWidth = isFilterWithOptions ? filter?.fullWidth : true;

        // Use a ref callback instead of an explicit ref object.
        const refCallback: FilterRefCallback = (element: FilterElementType | null) => {
          setRefItem(filterConfig.name, element, filterConfig.elementType);
        };

        configuredFilters.push({
          ...filterConfig,
          refCallback,
          label,
          fullWidth,
        });
      }
    }

    return configuredFilters;
  }, [t, filters, setRefItem]);

  const onApplyFilters = useCallback(() => {
    if (typeof siteId !== 'undefined') {
      dispatch(saveFilters(siteId));
    }
    dispatch(closeSidePanel());
  }, [dispatch, siteId]);

  const isDefaultValues = !useHasDraftFilters(filterGroup);

  return (
    <DrawerPanel
      // Reset the filter panel form state when the site is changed.
      key={siteId ?? null}
      header={t('Filters')}
      footer={
        <>
          <VdsButton onClick={onApplyFilters} variant="primary" size="medium" isFullWidth>
            {t('ApplyFilters')}
          </VdsButton>
          <VdsButton
            onClick={onClearAll}
            disabled={isDefaultValues}
            variant="ghost"
            size="medium"
            isFullWidth
          >
            {t('ClearAll')}
          </VdsButton>
        </>
      }
    >
      <DrawerForm columns={2}>
        {configuredFilters.map(
          ({ name, FilterComponent, refCallback, label, fullWidth = true }: ConfiguredFilter) => {
            return (
              <div
                key={name}
                className={styles.field}
                style={{ gridColumn: fullWidth ? 'span 2' : 'span 1' }}
              >
                <FilterComponent elementRef={refCallback} label={label} />
              </div>
            );
          },
        )}
      </DrawerForm>
    </DrawerPanel>
  );
}

export default FilterPanel;
